import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { getToken, setBaseId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Global base selector. Persists the chosen base in localStorage and keeps the
// api module in sync so the base_id query param is injected on every request.
const KEY = "lq3_base_id";
const Ctx = createContext(null);

export function BaseProvider({ children }) {
  const { user } = useAuth();
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBaseId, setSelected] = useState(() => localStorage.getItem(KEY) || "");

  useEffect(() => { setBaseId(selectedBaseId); }, [selectedBaseId]);

  const setSelectedBaseId = useCallback((id) => {
    const v = id || "";
    setSelected(v);
    if (v) localStorage.setItem(KEY, v); else localStorage.removeItem(KEY);
    setBaseId(v);
  }, []);

  const refetch = useCallback(async () => {
    if (!getToken()) { setBases([]); setLoading(false); return; }
    try {
      // skipBaseId: the bases dropdown must always list every base, regardless of the current filter.
      const { data } = await api.get("/admin/bases", { skipBaseId: true });
      setBases(data?.bases || []);
    } catch (_) {
      setBases([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [user, refetch]);

  return (
    <Ctx.Provider value={{ bases, loading, selectedBaseId, setSelectedBaseId, refetch }}>
      {children}
    </Ctx.Provider>
  );
}

export const useBase = () =>
  useContext(Ctx) || { bases: [], loading: false, selectedBaseId: "", setSelectedBaseId: () => {}, refetch: () => {} };
